import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import type { Database } from "../types/database";

export type Employee = Database["public"]["Tables"]["employees"]["Row"];
export type EmployeeInsert =
  Database["public"]["Tables"]["employees"]["Insert"];
export type EmployeeUpdate =
  Database["public"]["Tables"]["employees"]["Update"];

export type UserRole = Database["public"]["Tables"]["user_roles"]["Row"] & {
  roles?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
  facilities?: {
    id: string;
    name: string;
  } | null;
  group_homes?: {
    id: string;
    name: string;
  } | null;
};

export type EmployeeWithRoles = Employee & {
  user_roles?: UserRole[];
};

/**
 * Fetch employees list
 * @param facilityId - Optional facility ID to filter employees by facility
 * @param status - Employee status filter (default: 'active')
 * @param enabled - Whether to enable the query (default: true). Set to false to prevent API call.
 */
export function useEmployees(
  facilityId?: string,
  status: string = "active",
  enabled = true,
) {
  return useQuery({
    queryKey: ["employees", facilityId, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (facilityId) params.append("facility_id", facilityId);

      const { data } = await api.get(`/employees?${params.toString()}`);
      return data.data as Employee[];
    },
    enabled,
  });
}

/**
 * Fetch employees eligible across ALL of the given facilities (intersection).
 *
 * Used by the multi-facility shift-template picker: a template assigns ONE
 * employee, so only staff eligible at every selected facility are valid. The
 * backend applies the same facility/group-home eligibility rule as the single
 * `facility_id` path (SCRUM-135/136). Disabled until at least one facility is
 * chosen so the picker can't list org-wide staff.
 */
export function useEmployeesForFacilities(
  facilityIds: string[],
  status: string = "active",
) {
  const key = [...facilityIds].sort().join(",");
  return useQuery({
    queryKey: ["employees", "facilities", key, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      params.append("facility_ids", key);

      const { data } = await api.get(`/employees?${params.toString()}`);
      return data.data as Employee[];
    },
    enabled: facilityIds.length > 0,
  });
}

/**
 * Fetch single employee by ID
 */
export function useEmployee(id: string) {
  return useQuery({
    queryKey: ["employees", id],
    queryFn: async () => {
      const { data } = await api.get(`/employees/${id}`);
      return data.data as EmployeeWithRoles;
    },
    enabled: !!id,
  });
}

/**
 * Fetch the current user's OWN employee/profile record.
 *
 * Hits GET /api/auth/me/employee — a self route that works for every role
 * (including platform admins), unlike useEmployee() which calls the portal-
 * scoped /api/employees/:id and 403s platform users. Use this for Settings →
 * Profile; use useEmployee(id) only for managers viewing OTHER staff.
 *
 * Returns null when the user has no employee record yet (e.g. a platform admin
 * who has never saved a profile).
 */
export function useMyProfile() {
  return useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => {
      const { data } = await api.get("/auth/me/employee");
      return data.data as Employee | null;
    },
  });
}

/**
 * Create employee mutation
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (employee: EmployeeInsert) => {
      const { data } = await api.post("/employees", employee);
      return data.data as Employee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
    // US-081: EmployeeForm owns error UX via handleFormErrors (single signal).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Update employee mutation
 */
export function useUpdateEmployee(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (employee: EmployeeUpdate) => {
      const { data } = await api.put(`/employees/${employeeId}`, employee);
      return data.data as Employee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employees", employeeId] });
    },
    // US-081: EmployeeForm owns error UX via handleFormErrors (single signal).
    meta: FORM_MUTATION_META,
  });
}

/**
 * Delete employee mutation (soft delete)
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; EmployeeDetailPage catch is sole caller.
    meta: { errorFallback: "Failed to terminate employee" },
    mutationFn: async (employeeId: string) => {
      const { data } = await api.delete(`/employees/${employeeId}`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

/**
 * US-178 (F-133) — reset a locked-out employee's password.
 *
 * Returns a TEMPORARY password ONCE. It is never stored and never fetched
 * again: the caller reads it to the employee, who must replace it at next
 * sign-in. Do not put it in a query cache, a URL, or a log.
 */
export function useResetEmployeePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { errorFallback: "Failed to reset password" },
    mutationFn: async (employeeId: string) => {
      const { data } = await api.post(
        `/employees/${employeeId}/reset-password`,
      );
      return data.data as {
        temporary_password: string;
        employee: { id: string; email: string; name: string };
        message: string;
      };
    },
    onSuccess: () => {
      // The employee row itself is unchanged, but their sessions are gone —
      // refresh anything showing access state.
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });
}

/**
 * Fetch employee roles
 */
export function useEmployeeRoles(employeeId: string) {
  return useQuery({
    queryKey: ["employees", employeeId, "roles"],
    queryFn: async () => {
      const { data } = await api.get(`/employees/${employeeId}/roles`);
      return data.data as UserRole[];
    },
    enabled: !!employeeId,
  });
}

/**
 * Assign role to employee mutation
 */
export function useAssignRole(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // The assign-role modal owns error UX: a 409 ROLE_CONFLICT must open a
    // confirm-then-replace dialog rather than fire the generic global toast,
    // and MutationCache.onError can't be cancelled after the fact — so opt out
    // up front (US-081 pattern) and surface other errors from the modal.
    meta: { suppressErrorNotification: true },
    mutationFn: async (roleData: {
      role_id: string;
      facility_id?: string;
      group_home_id?: string;
      replace?: boolean;
    }) => {
      const { data } = await api.post(
        `/employees/${employeeId}/roles`,
        roleData,
      );
      return data.data as UserRole;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees", employeeId] });
      queryClient.invalidateQueries({
        queryKey: ["employees", employeeId, "roles"],
      });
    },
  });
}

/**
 * Remove role from employee mutation
 */
export function useRemoveRole(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; EmployeeDetailPage inline onError removed.
    meta: { errorFallback: "Failed to remove role" },
    mutationFn: async (assignmentId: string) => {
      const { data } = await api.delete(
        `/employees/${employeeId}/roles?assignment_id=${assignmentId}`,
      );
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees", employeeId] });
      queryClient.invalidateQueries({
        queryKey: ["employees", employeeId, "roles"],
      });
    },
  });
}
